import { LengthUnits } from "../../units/length";
import {
  FLUID_SPECIFIC_GRAVITY,
  WATER_DENSITY_LB_FT3,
  STEEL_DENSITY_LB_FT3,
  TUBING_JOINT_LENGTH_FT,
  COUPLING_LENGTH_FT,
  EQUIPMENT_NECK_LENGTH_FT
} from "./constants";
import type { StressStringSource, StressStringItem } from "./types";

// ── Helpers ─────────────────────────────────────────────────────────────────
export const toRadians = (deg: number) => (deg * Math.PI) / 180;

/** The sheet rounds every item boundary and length to 1 dp. */
export const roundToTenth = (value: number) => Math.round(value * 10) / 10;

/**
 * `L4` - `1-((C6*62.428)/490)`, added in v1.3.
 *
 * The fraction of its dry weight that a steel string keeps when it hangs in a
 * fluid of the given specific gravity. Falls back to the sheet's own default
 * when the scenario has no liquid SG yet, so the seed never silently loses
 * buoyancy.
 */
export function calculateBuoyancyFactor(fluidSpecificGravity?: number): number {
  const specificGravity =
    typeof fluidSpecificGravity === "number" && fluidSpecificGravity > 0
      ? fluidSpecificGravity
      : FLUID_SPECIFIC_GRAVITY;
  return 1 - (specificGravity * WATER_DENSITY_LB_FT3) / STEEL_DENSITY_LB_FT3;
}

/**
 * `AB` - `lambda_b`, columns `R` through `AA`.
 *
 * `contactRatio` is the group `2*N*Q*I/H` that recurs through the block. The
 * sheet writes it out in full in every cell; we name it once.
 *
 * The three branches are, in the sheet's order:
 * - `R > 0`: the joint is in tension contact, so use `S` (`lambda_bt`).
 * - `T > 0`: the joint is in compression contact, so use `Z` (`lambda_bc`).
 * - otherwise: no contact, so use `AA` (`lambda_bn`).
 */
export function calculateStressMultiplier(
  phi: number,
  contactRatio: number
): number {
  const cosh = Math.cosh(phi);
  const sinh = Math.sinh(phi);
  const cos = Math.cos(phi);
  const sin = Math.sin(phi);

  // R - tension contact
  const tensionContact = 1 - cosh + (phi / 2 - contactRatio) * sinh;

  // S - lambda_bt. The sheet writes `SINH(P-P)`, which is always sinh(0) = 0.
  const tensionMultiplier =
    tensionContact >= 0
      ? (phi * (0 - (phi / 2 + contactRatio) * (cosh - 1))) /
        (2 * (cosh - 1) - phi * sinh)
      : phi * (cosh / sinh);

  // T - compression contact
  const compressionContact = 1 - cos + (phi / 2 - contactRatio) * sin;

  // The shared denominator of U, V and W
  const denominator = 2 * (1 - cos) - phi * sin;
  if (denominator === 0) return tensionContact > 0 ? tensionMultiplier : 0;

  const offset = contactRatio - phi / 2;
  const couplingTerm = (phi * offset * (1 - cos + phi - sin)) / denominator; // U
  const midpointTerm =
    (phi * (-offset * (1 - cos) + phi * cos - sin)) / denominator; // V
  const rTerm = (phi * (offset * sin + 1 - cos)) / denominator; // W

  const omega = couplingTerm === 0 ? 0 : Math.atan(rTerm / couplingTerm); // X
  const stringTerm = // Y
    omega > 0 && omega < phi
      ? couplingTerm * Math.cos(omega) - rTerm * Math.sin(omega)
      : 0;

  // Z - lambda_bc
  const compressionMultiplier =
    compressionContact >= 0
      ? Math.max(
          Math.abs(couplingTerm),
          Math.abs(midpointTerm),
          Math.abs(stringTerm)
        )
      : phi / sin;

  // AA - lambda_bn
  const noContactMultiplier = phi / Math.tanh(phi);

  if (tensionContact > 0) return tensionMultiplier;
  if (compressionContact > 0) return compressionMultiplier;
  return noContactMultiplier;
}

/**
 * Rows 8..26 of the sheet: one tubing joint, then the equipment string.
 *
 * The wide and narrow sections sit at opposite ends of the two item kinds:
 *
 * - Tubing, row 8. `O8 = C5` and `F8 = O8 - G4 + H4`, so the wide **coupling**
 *   is the first 0.5 ft at the **top** of the joint. Below it the string is on
 *   the tubing body.
 * - Equipment, rows 9+. `O_n = ROUND(O_prev + E_n/12, 1)` and `F_n = O_n - 0.5`,
 *   so the narrow **neck** is the last 0.5 ft at the **bottom** of the item.
 *
 * The tubing coupling is modelled as an annulus of the coupling over the tubing
 * body: `K8 = G9/2` but `L8 = D9/2`, not the tubing ID.
 */
export function buildStressStringItems(
  source: StressStringSource
): StressStringItem[] {
  const {
    tubingLengthFt,
    tubingCouplingOuterDiameterInches,
    tubingOuterDiameterInches,
    tubingInnerDiameterInches,
    tubingPoundPerFoot,
    equipment
  } = source;

  const tubingStart = tubingLengthFt - TUBING_JOINT_LENGTH_FT;

  const items: StressStringItem[] = [
    {
      startDepthFt: tubingStart,
      wideSectionEndDepthFt: tubingStart + COUPLING_LENGTH_FT,
      endDepthFt: tubingLengthFt,
      jointLengthFt: TUBING_JOINT_LENGTH_FT,
      weightPerInchLb: tubingPoundPerFoot / 12,
      wideOuterRadiusInches: tubingCouplingOuterDiameterInches / 2,
      wideInnerRadiusInches: tubingOuterDiameterInches / 2,
      narrowOuterRadiusInches: tubingOuterDiameterInches / 2,
      narrowInnerRadiusInches: tubingInnerDiameterInches / 2
    }
  ];

  let previousEndDepthFt = tubingLengthFt;

  equipment.forEach(item => {
    const lengthInches = item.length_inches ?? 0;
    if (lengthInches <= 0) return;

    const startDepthFt = previousEndDepthFt;
    const endDepthFt = roundToTenth(
      previousEndDepthFt + LengthUnits.Inches.toStandardUnit(lengthInches)
    );

    items.push({
      startDepthFt,
      wideSectionEndDepthFt: endDepthFt - EQUIPMENT_NECK_LENGTH_FT,
      endDepthFt,
      jointLengthFt: roundToTenth(
        LengthUnits.Inches.toStandardUnit(lengthInches)
      ),
      weightPerInchLb: (item.weight_pounds ?? 0) / lengthInches,
      wideOuterRadiusInches: (item.outer_diameter_inches ?? 0) / 2,
      wideInnerRadiusInches: (item.inner_diameter_inches ?? 0) / 2,
      narrowOuterRadiusInches: (item.neck_outer_diameter_inches ?? 0) / 2,
      narrowInnerRadiusInches: (item.neck_inner_diameter_inches ?? 0) / 2
    });

    previousEndDepthFt = endDepthFt;
  });

  return items;
}

/**
 * `H`, `J`, `K` and `L` at a measured depth.
 *
 * The sheet walks the item list from the top and takes the first item whose
 * `F` or `O` boundary the depth has not yet passed. That gives the wide section
 * up to `F`, then the narrow section up to `O`.
 *
 * Sheet defect D7: `H34`, `K34`, `L34` and `M34` all compare with `<=`, but the
 * `J34` chain compares with `<=` for the first four items and `<` for the rest.
 * The two cannot both be right, so we use `<=` everywhere. On the pass thru
 * `Final_Landing` that changes 3 rows out of 1517 (sheet rows 1040, 1204 and
 * 1533), the boundary rows where the neighbouring items have different
 * weights. The accumulated axial load moves by at most 1.04 lb in 28,280
 * (3.7e-5, row 1204), and by 0.19 lb in 29,055 at the bottom of the string.
 * `Final_Traverse` has no such row, because its `C5` is off the 0.1 ft grid.
 */
export function getStressStringItemAtMD(
  measuredDepthFt: number,
  items: StressStringItem[]
): {
  jointLengthFt: number;
  weightPerInchLb: number;
  outerRadiusInches: number;
  innerRadiusInches: number;
} | null {
  for (const item of items) {
    if (measuredDepthFt <= item.wideSectionEndDepthFt) {
      return {
        jointLengthFt: item.jointLengthFt,
        weightPerInchLb: item.weightPerInchLb,
        outerRadiusInches: item.wideOuterRadiusInches,
        innerRadiusInches: item.wideInnerRadiusInches
      };
    }
    if (measuredDepthFt <= item.endDepthFt) {
      return {
        jointLengthFt: item.jointLengthFt,
        weightPerInchLb: item.weightPerInchLb,
        outerRadiusInches: item.narrowOuterRadiusInches,
        innerRadiusInches: item.narrowInnerRadiusInches
      };
    }
  }
  return null;
}
